/**
 * UpSurv 2.0 — the export/import bridge (DECISIONS D-020).
 *
 * ══ What this exists to fix ══════════════════════════════════════════════════
 *
 * iOS gives a Home Screen web app its own storage container, separate from
 * Safari's: same origin, same phone, different IndexedDB (D-019). Work done on
 * one side is invisible from the other and NOTHING migrates. There is no web API
 * that reaches across that wall — no shared storage, no handoff, no "copy my
 * IndexedDB". The only bridge that exists is the user: write a file out of one
 * container, read it back into the other.
 *
 * The same file is the BACKUP an unlinked device has never had. A device that
 * has not been linked holds the only copy of a day's work; clearing site data
 * loses it, and there is nothing else to restore from. One feature answers both,
 * because they are the same act.
 *
 * ══ The container format, and why it is not JSON ═════════════════════════════
 *
 * A survey is answers (small) plus photos (nearly all of it — forty JPEGs is a
 * hundred megabytes). Base64 inside JSON would have been three lines of code and
 * is the obvious thing; it is also how this feature would have crashed the phone
 * it exists to rescue:
 *
 *   · base64 inflates by 33%, so 100 MB of photos becomes a 133 MB STRING that
 *     must exist contiguously in memory, and then a second copy inside the Blob;
 *   · reading it back means the same again, in a browser that is already the
 *     memory-tightest environment the app runs in.
 *
 * So the file is a header and then the bytes, unaltered:
 *
 *   ┌─────────────────────────────────────────────────────────────────────────┐
 *   │ "UPSURV-EXPORT-1\n"          16 bytes, ASCII. The magic, and the format  │
 *   │                              version, in the one place a reader can see  │
 *   │                              before it has parsed anything.              │
 *   │ "0000001234\n"               11 bytes. The header's length in bytes,     │
 *   │                              zero-padded so the offset of the JSON is a  │
 *   │                              constant and not a scan.                    │
 *   │ {…header JSON…}              the documents and the photo directory.      │
 *   │ <photo bytes><photo bytes>…  concatenated, in header order.              │
 *   └─────────────────────────────────────────────────────────────────────────┘
 *
 * Neither direction holds the pictures in JavaScript (final review's fix wave,
 * D-040 postscript — the sentence here used to say "O(1)", which was true of
 * version-1 Blob rows and stopped being true when D-040 §2 made the rows
 * buffers). On the way OUT each row is walked with a cursor and wrapped in its
 * own `Blob` the moment it is read: the Blob constructor copies a buffer's
 * bytes into the browser's blob storage — Chromium and WebKit both snapshot
 * a BufferSource, there is no zero-copy shape — but that storage is outside
 * the JS heap and pageable, and the row's own buffer is garbage as soon as
 * the Blob exists, so the high-water mark is one picture rather than the sum
 * of them. On the way back IN `Blob.slice()` hands a lazy view to the store,
 * which reads and writes ONE SURVEY'S pictures at a time (`importSurveys`).
 * That is what makes an "everything on this device" export safe on a phone
 * holding four hundred megabytes of machine rooms.
 *
 * ══ Validate everything, then write survey by survey ═════════════════════════
 *
 * `readTransfer` parses and validates the WHOLE file — every document through
 * `SurveyDocumentSchema`, every photo offset against the file's real size —
 * before a single row is written, so a file that cannot be trusted writes
 * nothing. `applyTransfer` then commits one Dexie transaction PER SURVEY:
 * there is no state in which a survey exists without the photos that belong
 * to it, and a survey whose write fails is refused by name without sinking
 * the surveys around it.
 *
 * ══ Deterministic ════════════════════════════════════════════════════════════
 *
 * The clock comes from the store, ids come from the documents. Two exports of
 * the same device at the same instant are byte-identical.
 */

import { safeParseSurvey } from '../core/schema';
import { SCHEMA_VERSION, type SurveyDocument } from '../core/types';
import {
  hasPhotoBuffer,
  isoAt,
  type ImportRefusal,
  type ImportedSurvey,
  type StoredSurvey,
  type SurveyStore,
} from './store';

/* ═══════════════════════════════ the format ═══════════════════════════════ */

/** First line of every export. Bumped only by a change readers cannot survive. */
export const TRANSFER_MAGIC = 'UPSURV-EXPORT-1\n';

/** The version inside the header. Readers accept exactly this. */
export const TRANSFER_FORMAT_VERSION = 1;

/** Digits in the zero-padded header-length line, plus its newline. */
const LENGTH_DIGITS = 10;

/** Where the header JSON starts. A constant, which is the point of the padding. */
export const TRANSFER_PREFIX_BYTES = TRANSFER_MAGIC.length + LENGTH_DIGITS + 1;

/** The extension the file is written with. */
export const TRANSFER_EXTENSION = '.upsurv';

/**
 * One photo in the file's directory: where its bytes are, and everything
 * `StoredPhotoBlob` needs to be rebuilt without opening the document.
 *
 * `offset` is relative to the START OF THE PAYLOAD, not to the start of the
 * file. The header cannot know its own length until it has been serialised, and
 * a header carrying absolute offsets would change length when they changed — a
 * fixed point nobody needs to solve for.
 */
export interface TransferPhotoEntry {
  photoId: string;
  contentType: string;
  bytes: number;
  capturedAt: string;
  offset: number;
  length: number;
}

/** One survey in the file: the document, its sync history, its photo directory. */
export interface TransferSurveyEntry {
  surveyId: string;
  document: SurveyDocument;
  serverRevision: number;
  sealed: boolean;
  creationNotified: boolean;
  lastSyncedAt?: string;
  photos: TransferPhotoEntry[];
  /**
   * Photos in the document whose bytes this device no longer had — already
   * uploaded and purged, in practice. Recorded so the import can SAY so instead
   * of the surveyor discovering a gap later.
   */
  photosWithoutBytes: number;
}

export interface TransferHeader {
  format: 'upsurv.export';
  formatVersion: number;
  /** ISO instant from the store's clock. */
  exportedAt: string;
  /** The schema version the writing build spoke. Diagnostic, not a gate. */
  appSchemaVersion: string;
  surveys: TransferSurveyEntry[];
}

/* ═══════════════════════════════ problems ═══════════════════════════════ */

/**
 * Why a file could not be read. A KIND, never a sentence: the sentences live in
 * src/ui/state/transferView.ts with the rest of the copy, so the same failure
 * cannot be worded two ways in two places.
 *
 *   not-an-export      the magic line is missing — a photo, a PDF, the wrong file
 *   damaged            the magic is there and the rest is not readable
 *   truncated          the file is shorter than its own header says it is
 *   unsupported        a format version this build does not know
 *   invalid-document   a survey inside it failed `SurveyDocumentSchema`
 *   empty              a valid file with no surveys in it
 */
export type TransferProblemKind =
  | 'not-an-export'
  | 'damaged'
  | 'truncated'
  | 'unsupported'
  | 'invalid-document'
  | 'empty';

export interface TransferProblem {
  kind: TransferProblemKind;
  /** The technical reason, for the office and for a bug report. Never alone. */
  detail: string;
}

export interface TransferBundle {
  header: TransferHeader;
  /** The file itself, kept so photo bytes can be sliced lazily at import time. */
  file: Blob;
  /** Absolute offset of the payload region within `file`. */
  payloadAt: number;
}

export type TransferReadResult =
  | { ok: true; bundle: TransferBundle }
  | { ok: false; problem: TransferProblem };

/* ════════════════════════════════ writing ════════════════════════════════ */

export interface TransferEstimate {
  surveys: number;
  photos: number;
  /** Photo bytes, which is essentially the whole file. */
  photoBytes: number;
  /** Photo bytes plus a small allowance for the documents. */
  totalBytes: number;
  /** Photos whose bytes are gone from this device (uploaded, then purged). */
  photosWithoutBytes: number;
}

/** Documents are small next to photos, but not nothing. 8 KB each is generous. */
const DOCUMENT_ALLOWANCE_BYTES = 8 * 1024;

/**
 * How big the file will be, WITHOUT building it.
 *
 * Asked before the button is pressed, so the screen can say "about 34 MB" up
 * front. An export that silently produces a 200 MB file on a phone is a feature
 * that gets blamed for the phone, and a surveyor who was told first is a
 * surveyor who chose.
 */
export async function estimateTransfer(
  store: SurveyStore,
  surveyIds: readonly string[],
): Promise<TransferEstimate> {
  let photos = 0;
  let photoBytes = 0;
  let photosWithoutBytes = 0;

  for (const surveyId of surveyIds) {
    const record = await store.getSurvey(surveyId);
    if (!record) continue;
    const held = new Set<string>();
    // A cursor rather than `toArray()`: since schema version 2 a row is its
    // picture, and the estimate should not hold a survey's photos in memory.
    await store.db.photoBlobs.where('surveyId').equals(surveyId).each((row) => {
      // A row with no buffer is not held: the file will carry nothing for it,
      // exactly as for a photo with no row at all.
      if (!hasPhotoBuffer(row)) return;
      held.add(row.photoId);
      photos += 1;
      // The bytes the file will actually carry, not the count the PhotoRef declared.
      photoBytes += row.data.byteLength;
    });
    for (const photo of record.document.photos) {
      if (!held.has(photo.id)) photosWithoutBytes += 1;
    }
  }

  return {
    surveys: surveyIds.length,
    photos,
    photoBytes,
    totalBytes: photoBytes + surveyIds.length * DOCUMENT_ALLOWANCE_BYTES,
    photosWithoutBytes,
  };
}

export interface TransferFile {
  blob: Blob;
  fileName: string;
  bytes: number;
  surveys: number;
  photos: number;
  photosWithoutBytes: number;
}

/**
 * Build the file. Each photo row is read with a cursor and wrapped in its own
 * `Blob` at once, so what the output is assembled from is a list of Blob
 * handles in browser storage, never the rows' buffers held in an array (see
 * the module header). The rows are read in the table's order and the file is
 * written in DOCUMENT order, so the per-photo Blobs are kept by id until the
 * document walk places them.
 */
export async function buildTransfer(
  store: SurveyStore,
  surveyIds: readonly string[],
): Promise<TransferFile> {
  const entries: TransferSurveyEntry[] = [];
  const payload: BlobPart[] = [];
  let offset = 0;
  let photos = 0;
  let photosWithoutBytes = 0;

  for (const surveyId of surveyIds) {
    const record = await store.getSurvey(surveyId);
    if (!record) continue;

    // A cursor, and a Blob per row the moment it is read: the row's buffer is
    // released as the cursor moves on, and only the Blob handle is kept. A row
    // with no buffer is left out, so its photo gets the same treatment as one
    // with no row: counted "without bytes", not in the file.
    const byId = new Map<string, { part: Blob; meta: Omit<TransferPhotoEntry, 'offset' | 'length'> }>();
    await store.db.photoBlobs.where('surveyId').equals(surveyId).each((row) => {
      if (!hasPhotoBuffer(row)) return;
      byId.set(row.photoId, {
        part: new Blob([row.data]),
        meta: {
          photoId: row.photoId,
          contentType: row.contentType,
          bytes: row.bytes,
          capturedAt: row.capturedAt,
        },
      });
    });
    const directory: TransferPhotoEntry[] = [];

    // Walked in DOCUMENT order rather than in whatever order the table returns,
    // so two exports of one device produce byte-identical files.
    for (const photo of record.document.photos) {
      const stored = byId.get(photo.id);
      if (!stored) {
        photosWithoutBytes += 1;
        continue;
      }
      const length = stored.part.size;
      directory.push({ ...stored.meta, offset, length });
      payload.push(stored.part);
      offset += length;
      photos += 1;
    }
    byId.clear();

    entries.push({
      surveyId,
      document: record.document,
      serverRevision: record.serverRevision,
      sealed: record.sealed === 1,
      creationNotified: record.creationNotified === 1,
      ...(record.lastSyncedAt ? { lastSyncedAt: record.lastSyncedAt } : {}),
      photos: directory,
      photosWithoutBytes: record.document.photos.length - directory.length,
    });
  }

  const header: TransferHeader = {
    format: 'upsurv.export',
    formatVersion: TRANSFER_FORMAT_VERSION,
    exportedAt: isoAt(store.now()),
    appSchemaVersion: SCHEMA_VERSION,
    surveys: entries,
  };

  const headerBytes = new TextEncoder().encode(JSON.stringify(header));
  const lengthLine = String(headerBytes.length).padStart(LENGTH_DIGITS, '0');
  if (lengthLine.length > LENGTH_DIGITS) {
    // Ten digits is 10 GB of header — unreachable, since the header holds no
    // image bytes. Loud rather than silently writing a file no reader can parse.
    throw new Error('buildTransfer: header is too large to describe');
  }

  const blob = new Blob([TRANSFER_MAGIC, `${lengthLine}\n`, headerBytes, ...payload], {
    type: 'application/octet-stream',
  });

  return {
    blob,
    fileName: transferFileName(entries, header.exportedAt),
    bytes: blob.size,
    surveys: entries.length,
    photos,
    photosWithoutBytes,
  };
}

/**
 * A file name a person can find again in a Files app three weeks later: what it
 * is, which building, and when. One survey is named for the building; several
 * are named for the phone they came off.
 */
export function transferFileName(
  entries: readonly TransferSurveyEntry[],
  exportedAt: string,
): string {
  const day = exportedAt.slice(0, 10);
  const single = entries.length === 1 ? entries[0] : undefined;
  const title = single ? slug(buildingTitle(single.document)) : '';
  const middle = title !== '' ? title : entries.length === 1 ? 'survey' : 'all-surveys';
  return `upsurv-${middle}-${day}${TRANSFER_EXTENSION}`;
}

/** B1 (building name), else B2 (address), else nothing. */
export function buildingTitle(document: SurveyDocument): string {
  for (const questionId of ['B1', 'B2']) {
    const answer = document.answers[questionId];
    if (answer?.kind === 'text' && answer.value.trim() !== '') return answer.value.trim();
  }
  return '';
}

function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

/* ════════════════════════════════ reading ════════════════════════════════ */

function problem(kind: TransferProblemKind, detail: string): TransferReadResult {
  return { ok: false, problem: { kind, detail } };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Read and FULLY validate a file. Nothing is written; nothing can be, because
 * this function has no store.
 *
 * Every check that can fail happens here, in this order, because the order is
 * what makes the message useful: is it one of ours at all → can we find the
 * header → do we speak its version → is every document a valid survey → do the
 * photo offsets fit inside the file we are actually holding.
 */
export async function readTransfer(file: Blob): Promise<TransferReadResult> {
  if (file.size < TRANSFER_PREFIX_BYTES) {
    return problem('not-an-export', `the file is only ${String(file.size)} bytes`);
  }

  const prefix = new TextDecoder().decode(await file.slice(0, TRANSFER_PREFIX_BYTES).arrayBuffer());
  if (!prefix.startsWith(TRANSFER_MAGIC)) {
    return problem('not-an-export', 'the file does not begin with an UpSurv export header');
  }

  const lengthLine = prefix.slice(TRANSFER_MAGIC.length, TRANSFER_PREFIX_BYTES - 1);
  if (!/^\d+$/.test(lengthLine)) {
    return problem('damaged', 'the header length line is not a number');
  }
  const headerLength = Number(lengthLine);
  const payloadAt = TRANSFER_PREFIX_BYTES + headerLength;
  if (payloadAt > file.size) {
    return problem(
      'truncated',
      `the header says ${String(headerLength)} bytes but the file holds ` +
        `${String(file.size - TRANSFER_PREFIX_BYTES)}`,
    );
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(
      new TextDecoder().decode(await file.slice(TRANSFER_PREFIX_BYTES, payloadAt).arrayBuffer()),
    );
  } catch (cause) {
    return problem('damaged', cause instanceof Error ? cause.message : 'the header is not JSON');
  }

  if (!isRecord(parsed) || parsed['format'] !== 'upsurv.export') {
    return problem('damaged', 'the header is not an UpSurv export header');
  }
  if (parsed['formatVersion'] !== TRANSFER_FORMAT_VERSION) {
    return problem(
      'unsupported',
      `the file is format version ${JSON.stringify(parsed['formatVersion'])}; this app ` +
        `reads version ${String(TRANSFER_FORMAT_VERSION)}`,
    );
  }

  const rawSurveys = parsed['surveys'];
  if (!Array.isArray(rawSurveys)) return problem('damaged', 'the header lists no surveys');
  if (rawSurveys.length === 0) return problem('empty', 'the file contains no surveys');

  const surveys: TransferSurveyEntry[] = [];
  const payloadBytes = file.size - payloadAt;

  for (const raw of rawSurveys as unknown[]) {
    if (!isRecord(raw)) return problem('damaged', 'a survey entry is not an object');

    // THE GATE. Every document goes through `SurveyDocumentSchema` — the same
    // schema the sync engine and D1 enforce — before anything is written, so an
    // import can never put a document on this device that the app could not
    // have produced itself.
    const document = safeParseSurvey(raw['document']);
    if (!document.success) {
      return problem('invalid-document', document.error.message);
    }

    const photos: TransferPhotoEntry[] = [];
    const rawPhotos = raw['photos'];
    if (!Array.isArray(rawPhotos)) return problem('damaged', 'a survey entry has no photo list');
    const known = new Set(document.data.photos.map((photo) => photo.id));

    for (const entry of rawPhotos as unknown[]) {
      if (!isRecord(entry)) return problem('damaged', 'a photo entry is not an object');
      const photoId = entry['photoId'];
      const offset = entry['offset'];
      const length = entry['length'];
      if (typeof photoId !== 'string' || typeof offset !== 'number' || typeof length !== 'number') {
        return problem('damaged', 'a photo entry is missing its id, offset or length');
      }
      if (!Number.isInteger(offset) || !Number.isInteger(length) || offset < 0 || length < 0) {
        return problem('damaged', `photo "${photoId}" has a nonsensical position in the file`);
      }
      if (offset + length > payloadBytes) {
        // The one check that catches a file cut short by a failed download or a
        // full disk: the directory promises bytes the file does not contain.
        return problem(
          'truncated',
          `photo "${photoId}" needs bytes ${String(offset)}–${String(offset + length)} and the ` +
            `file only holds ${String(payloadBytes)}`,
        );
      }
      if (!known.has(photoId)) {
        return problem('damaged', `photo "${photoId}" is not part of the survey it is filed under`);
      }
      photos.push({
        photoId,
        contentType: typeof entry['contentType'] === 'string' ? entry['contentType'] : 'image/jpeg',
        bytes: typeof entry['bytes'] === 'number' ? entry['bytes'] : length,
        capturedAt:
          typeof entry['capturedAt'] === 'string'
            ? entry['capturedAt']
            : document.data.updatedAt,
        offset,
        length,
      });
    }

    surveys.push({
      surveyId: document.data.surveyId,
      document: document.data,
      serverRevision: typeof raw['serverRevision'] === 'number' ? raw['serverRevision'] : 0,
      sealed: raw['sealed'] === true,
      creationNotified: raw['creationNotified'] === true,
      ...(typeof raw['lastSyncedAt'] === 'string' ? { lastSyncedAt: raw['lastSyncedAt'] } : {}),
      photos,
      photosWithoutBytes:
        typeof raw['photosWithoutBytes'] === 'number'
          ? raw['photosWithoutBytes']
          : document.data.photos.length - photos.length,
    });
  }

  return {
    ok: true,
    bundle: {
      header: {
        format: 'upsurv.export',
        formatVersion: TRANSFER_FORMAT_VERSION,
        exportedAt: typeof parsed['exportedAt'] === 'string' ? parsed['exportedAt'] : '',
        appSchemaVersion:
          typeof parsed['appSchemaVersion'] === 'string' ? parsed['appSchemaVersion'] : '',
        surveys,
      },
      file,
      payloadAt,
    },
  };
}

/* ════════════════════════════════ planning ═══════════════════════════════ */

/**
 * What importing this survey would do to THIS device.
 *
 *   'new'       nothing here has that id. It lands.
 *   'duplicate' this device already holds that survey. It is NEVER overwritten
 *               without being asked — the local copy may be the newer one, and
 *               silently replacing a day's work with a week-old backup is the
 *               exact failure this whole feature exists to prevent.
 *   'discarded' this device withdrew that survey and has not managed to tell the
 *               office yet. Importing it back would resurrect a survey whose
 *               removal is still an outstanding promise, so it is refused with
 *               an explanation rather than quietly re-added (D-012).
 */
export type ImportDisposition = 'new' | 'duplicate' | 'discarded';

export interface ImportPlanItem {
  surveyId: string;
  /** The building name, for a line the surveyor recognises. */
  title: string;
  disposition: ImportDisposition;
  submitted: boolean;
  /** True when the FILE holds it sealed — it imports as a sealed record. */
  sealed: boolean;
  photos: number;
  photosWithoutBytes: number;
  /**
   * When the survey IN THE FILE was last worked on — not when the file was
   * saved. A backup taken today of a survey untouched since Tuesday is a
   * Tuesday survey, and the duplicate question is decided on this stamp.
   */
  fileUpdatedAt: string;
  /** The survey already here, when there is one. Used to compare dates. */
  existing?: StoredSurvey;
}

export interface ImportPlan {
  items: ImportPlanItem[];
  exportedAt: string;
}

/** Read-only: what would happen, decided before anything is offered. */
export async function planTransfer(
  store: SurveyStore,
  bundle: TransferBundle,
): Promise<ImportPlan> {
  const items: ImportPlanItem[] = [];
  for (const entry of bundle.header.surveys) {
    const existing = await store.getSurvey(entry.surveyId);
    const discarded = await store.hasPendingDiscard(entry.surveyId);
    const title = buildingTitle(entry.document);
    items.push({
      surveyId: entry.surveyId,
      title: title === '' ? 'Untitled survey' : title,
      disposition: discarded ? 'discarded' : existing ? 'duplicate' : 'new',
      submitted:
        entry.document.status === 'submitted_clean' ||
        entry.document.status === 'submitted_with_gaps',
      sealed: entry.sealed,
      photos: entry.photos.length,
      photosWithoutBytes: entry.photosWithoutBytes,
      fileUpdatedAt: entry.document.updatedAt,
      ...(existing ? { existing } : {}),
    });
  }
  return { items, exportedAt: bundle.header.exportedAt };
}

/* ════════════════════════════════ applying ═══════════════════════════════ */

export interface ImportOutcome {
  imported: string[];
  skipped: string[];
  /** The photos the chosen surveys carried, counting those of a refused survey too. */
  photos: number;
  /** Surveys the phone could not write — none of their rows landed. */
  refused: ImportRefusal[];
}

/**
 * Write the chosen surveys. One transaction PER SURVEY (see
 * `SurveyStore.importSurveys`), so each survey is imported whole or not at
 * all, and one that cannot be written is named in `refused` while the rest
 * still land.
 *
 * `chosen` is the surveyor's answer to the duplicate question and nothing else
 * decides it here: this function never guesses which copy is better.
 */
export async function applyTransfer(
  store: SurveyStore,
  bundle: TransferBundle,
  chosen: ReadonlySet<string>,
): Promise<ImportOutcome> {
  const inputs: ImportedSurvey[] = [];
  const skipped: string[] = [];
  let photos = 0;

  for (const entry of bundle.header.surveys) {
    if (!chosen.has(entry.surveyId)) {
      skipped.push(entry.surveyId);
      continue;
    }
    inputs.push({
      document: entry.document,
      serverRevision: entry.serverRevision,
      sealed: entry.sealed,
      creationNotified: entry.creationNotified,
      ...(entry.lastSyncedAt ? { lastSyncedAt: entry.lastSyncedAt } : {}),
      photos: entry.photos.map((photo) => {
        photos += 1;
        return {
          photoId: photo.photoId,
          // Lazy. No image bytes pass through JavaScript in either direction.
          blob: bundle.file.slice(
            bundle.payloadAt + photo.offset,
            bundle.payloadAt + photo.offset + photo.length,
            photo.contentType,
          ),
          contentType: photo.contentType,
          bytes: photo.bytes,
          capturedAt: photo.capturedAt,
        };
      }),
    });
  }

  if (inputs.length === 0) return { imported: [], skipped, photos: 0, refused: [] };

  const { written, refused } = await store.importSurveys(inputs);
  return { imported: written.map((record) => record.surveyId), skipped, photos, refused };
}
